import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import GlassCard from '../components/GlassCard';

const TermsOfServicePage = () => {
  const { theme } = useTheme();

  return (
    <div style={{ padding: '32px 0', maxWidth: '900px', margin: '0 auto' }}>
      <Link
        to="/"
        style={{
          color: theme.text.secondary,
          textDecoration: 'none',
          marginBottom: '24px',
          display: 'inline-block',
          fontSize: '14px',
        }}
      >
        ← Back to Home
      </Link>

      <GlassCard>
        <h1 style={{ fontSize: '36px', marginBottom: '8px', color: theme.text.primary }}>
          Terms of Service – Family Tree
        </h1>
        <p style={{ color: theme.text.muted, fontSize: '14px', marginBottom: '32px' }}>
          Last updated: January 5, 2026
        </p>

        <div style={{ color: theme.text.secondary, lineHeight: '1.8', fontSize: '16px' }}>
          <p style={{ marginBottom: '24px' }}>
            Welcome to Family Tree ("we," "our," or "the app"). By accessing or using the Family Tree application, website, or related services, you agree to these Terms of Service ("Terms"). If you do not agree, please do not use the app.
          </p>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              1. Eligibility
            </h2>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li>You must be at least 13 years old to use Family Tree.</li>
              <li>If you are under 18, you must have permission from a parent or legal guardian.</li>
              <li>By using the app, you confirm that you meet these requirements.</li>
            </ul>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              2. Account Registration & Authentication
            </h2>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li>Family Tree uses Google Authentication for secure sign-in.</li>
              <li>You are responsible for maintaining the confidentiality of your account.</li>
              <li>You agree to provide accurate and current information.</li>
              <li>We are not responsible for unauthorized access caused by user negligence.</li>
            </ul>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              3. Use of the Service
            </h2>
            <p style={{ marginBottom: '16px' }}>
              Family Tree allows users to:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li>Create and manage family trees</li>
              <li>Add and edit family member information</li>
              <li>Collaborate with invited family members</li>
            </ul>
            <p style={{ marginBottom: '16px' }}>
              You agree to use the app lawfully and respectfully.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              4. User-Provided Content
            </h2>
            <p style={{ marginBottom: '16px' }}>
              You may upload or add content such as names, dates, photos, hobbies, and descriptions.
            </p>
            <p style={{ marginBottom: '16px' }}>
              By submitting content, you confirm that:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li>You have permission to share the information</li>
              <li>The content is accurate to the best of your knowledge</li>
              <li>The content does not violate any laws or rights</li>
            </ul>
            <p style={{ marginBottom: '16px' }}>
              You retain ownership of your content, but you grant Family Tree permission to display and process it solely for app functionality.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              5. Prohibited Activities
            </h2>
            <p style={{ marginBottom: '16px' }}>
              You agree not to:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li>Add false, misleading, or harmful information</li>
              <li>Upload content that violates privacy, intellectual property, or other rights</li>
              <li>Harass, impersonate, or abuse other users</li>
              <li>Attempt to access or interfere with the app's systems</li>
              <li>Use the app for illegal or commercial purposes</li>
            </ul>
            <p style={{ marginBottom: '16px' }}>
              We reserve the right to suspend or terminate accounts that violate these Terms.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              6. Privacy
            </h2>
            <p style={{ marginBottom: '16px' }}>
              Your use of Family Tree is governed by our Privacy Policy, available at:
            </p>
            <p style={{ marginBottom: '16px' }}>
              👉{' '}
              <Link
                to="/privacy"
                style={{ color: theme.accent.primary, textDecoration: 'none' }}
              >
                https://ourfamilytree.cloud/privacy
              </Link>
            </p>
            <p style={{ marginBottom: '16px' }}>
              Please review it to understand how we collect and protect your information.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              7. Data Accuracy & Family Relationships
            </h2>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li>Family Tree does not verify family relationships or personal details.</li>
              <li>All information is user-generated and provided at the user's discretion.</li>
              <li>We are not responsible for disputes, inaccuracies, or disagreements between family members.</li>
            </ul>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              8. Account Termination
            </h2>
            <p style={{ marginBottom: '16px' }}>
              You may stop using the app at any time and request account deletion.
            </p>
            <p style={{ marginBottom: '16px' }}>
              We may suspend or terminate access if:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li>These Terms are violated</li>
              <li>The app is misused</li>
              <li>Required by law</li>
            </ul>
            <p style={{ marginBottom: '16px' }}>
              Upon termination, your access to the app will end.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              9. Availability & Changes
            </h2>
            <p style={{ marginBottom: '16px' }}>
              We strive to keep Family Tree available, but we do not guarantee uninterrupted service.
            </p>
            <p style={{ marginBottom: '16px' }}>
              We may:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li>Modify or discontinue features</li>
              <li>Update these Terms</li>
            </ul>
            <p style={{ marginBottom: '16px' }}>
              Changes will be reflected by updating the "Last updated" date.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              10. Disclaimer of Warranties
            </h2>
            <p style={{ marginBottom: '16px' }}>
              Family Tree is provided "as is" and "as available."
            </p>
            <p style={{ marginBottom: '16px' }}>
              We make no guarantees that:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li>The app will be error-free</li>
              <li>All data will always be accurate</li>
              <li>The service will meet all expectations</li>
            </ul>
            <p style={{ marginBottom: '16px' }}>
              Use of the app is at your own risk.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              11. Limitation of Liability
            </h2>
            <p style={{ marginBottom: '16px' }}>
              To the fullest extent permitted by law, Family Tree shall not be liable for:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px' }}>
              <li>Data loss</li>
              <li>Disputes between users</li>
              <li>Indirect or incidental damages</li>
              <li>Unauthorized access beyond our control</li>
            </ul>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              12. Governing Law
            </h2>
            <p style={{ marginBottom: '16px' }}>
              These Terms are governed by the laws of the jurisdiction in which Family Tree operates, without regard to conflict of law principles.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
              13. Contact Information
            </h2>
            <p style={{ marginBottom: '16px' }}>
              For questions or concerns about these Terms, contact us at:
            </p>
            <ul style={{ marginLeft: '24px', marginBottom: '16px', listStyle: 'none' }}>
              <li style={{ marginBottom: '8px' }}>
                📧 Email:{' '}
                <a
                  href="mailto:twodotsai@gmail.com"
                  style={{ color: theme.accent.primary, textDecoration: 'none' }}
                >
                  twodotsai@gmail.com
                </a>
              </li>
              <li>
                🌐 Website:{' '}
                <a
                  href="https://ourfamilytree.cloud"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: theme.accent.primary, textDecoration: 'none' }}
                >
                  https://ourfamilytree.cloud
                </a>
              </li>
            </ul>
          </section>
        </div>
      </GlassCard>
    </div>
  );
};

export default TermsOfServicePage;






